import { createContext, useContext, useCallback, useEffect, useMemo, useState } from "react";
import {
  CART_STORAGE_KEY,
  Addon,
  CartItem,
  CatalogPlan,
  CatalogProduct,
  MAX_QUANTITY_PER_ITEM,
  cartTotals,
  isValidCartItem,
  makeCartItem,
  makeAddonCartItem,
  type CartTotals,
} from "@/lib/cart-pricing";

interface CartContextValue {
  items: CartItem[];
  totals: CartTotals;
  count: number;
  addItem: (product: CatalogProduct, plan: CatalogPlan, quantity?: number) => void;
  addAddon: (addon: Addon, quantity?: number) => void;
  removeItem: (key: string) => void;
  setQuantity: (key: string, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const readStoredCart = (): CartItem[] => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Drop only the invalid entries — valid items survive malformed storage.
    return parsed.filter(isValidCartItem).map((i) => ({
      ...i,
      quantity: Math.min(Math.max(1, Math.trunc(i.quantity)), MAX_QUANTITY_PER_ITEM),
    }));
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() =>
    typeof window === "undefined" ? [] : readStoredCart(),
  );

  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage unavailable — cart stays in-memory */
    }
  }, [items]);

  const addItem = useCallback((product: CatalogProduct, plan: CatalogPlan, quantity = 1) => {
    const next = makeCartItem(product, plan, Math.max(1, Math.trunc(quantity)));
    setItems((prev) => {
      const existing = prev.find((i) => i.key === next.key);
      if (existing) {
        return prev.map((i) =>
          i.key === next.key
            ? {
                ...i,
                unitPrice: next.unitPrice,
                quantity: Math.min(i.quantity + next.quantity, MAX_QUANTITY_PER_ITEM),
              }
            : i,
        );
      }
      return [...prev, next];
    });
  }, []);

  const addAddon = useCallback((addon: Addon, quantity = 1) => {
    const next = makeAddonCartItem(addon, Math.max(1, Math.trunc(quantity)));
    setItems((prev) => {
      const existing = prev.find((i) => i.key === next.key);
      if (existing) {
        return prev.map((i) =>
          i.key === next.key
            ? {
                ...i,
                productName: next.productName,
                productImage: next.productImage,
                unitPrice: next.unitPrice,
                quantity: Math.min(i.quantity + next.quantity, MAX_QUANTITY_PER_ITEM),
              }
            : i,
        );
      }
      return [...prev, next];
    });
  }, []);

  const removeItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const setQuantity = useCallback((key: string, quantity: number) => {
    const q = Math.min(Math.max(1, Math.trunc(quantity) || 1), MAX_QUANTITY_PER_ITEM);
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: q } : i)));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo<CartContextValue>(() => {
    const totals = cartTotals(items);
    return {
      items,
      totals,
      count: totals.units + totals.addonUnits,
      addItem,
      addAddon,
      removeItem,
      setQuantity,
      clearCart,
    };
  }, [items, addItem, addAddon, removeItem, setQuantity, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
